import { Subject, BehaviorSubject, Observable, of } from "rxjs";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { catchError, map } from "rxjs/operators";
import { appConfig } from "./app.config";

export type AppMode = "REPORTING" | "BACK_OFFICE";

@Injectable()
export class AppService {
  private readonly appModeStorageKey = "app.mode";
  private readonly backOfficeTokenStorageKey = "backoffice.token";
  private readonly appModeSource: BehaviorSubject<AppMode>;

  readonly appMode$: Observable<AppMode>;

  constructor(
    public router: Router,
    private http: HttpClient,
  ) {
    this.appMode = this.getStoredAppMode();
    this.appModeSource = new BehaviorSubject<AppMode>(this.appMode);
    this.appMode$ = this.appModeSource.asObservable();
  }

  appMode: AppMode = "REPORTING";
  userNameSource = new Subject<any>();
  updatedUserName$ = this.userNameSource.asObservable();
  companyLogoSource = new BehaviorSubject<any>("");
  updatedCompanyLogo$ = this.companyLogoSource.asObservable();
  navigationStateSource = new Subject<any>();
  updatedNavigationState$ = this.navigationStateSource.asObservable();
  userProfileImageSource = new BehaviorSubject<any>("");
  updatedUserProfileImage$ = this.userProfileImageSource.asObservable();
  companyDataSource = new BehaviorSubject<any>({});
  updatedCompanyData$ = this.companyDataSource.asObservable();
  userDataSource = new BehaviorSubject<any>({});
  updatedUserData$ = this.userDataSource.asObservable();
  countryCodeSource = new BehaviorSubject<any>("");
  updatedCompanyCode$ = this.countryCodeSource.asObservable();
  levelSource = new BehaviorSubject<any>("");
  updatedLevels$ = this.levelSource.asObservable();
  treeSource = new Subject();
  updatedTree$ = this.treeSource.asObservable();
  deleteNodeSource = new Subject();
  deleteNode$ = this.deleteNodeSource.asObservable();
  switchUserSource = new Subject();
  switchUser$ = this.switchUserSource.asObservable();
  updateCompanyMenu = new BehaviorSubject<boolean>(false);
  companyMenu$ = this.updateCompanyMenu.asObservable();
  errorSubject = new Subject<void>();
  getErrorSubject = this.errorSubject.asObservable();
  loggingInSubject = new Subject<void>();
  getLoggingInSubject = this.loggingInSubject.asObservable();
  updateWidthForReporting = new Subject<boolean>();
  pageScrollingSubject = new BehaviorSubject<boolean>(true);
  pageScrollingSubject$ = this.pageScrollingSubject.asObservable();
  openLoyaltyNumberScanner = new Subject<boolean>();
  updateLoyaltyNumberSearchInputSubject = new BehaviorSubject<string | null>(
    null
  );
  getUpdateLoyaltyNumberSearchInputSubject$ =
    this.updateLoyaltyNumberSearchInputSubject.asObservable();
  visibleLoyaltySearchInputs = new BehaviorSubject<any>(false);
  doorScanViewSubject = new Subject<string>();
  doorScanViewSubject$ = this.doorScanViewSubject.asObservable();
  loyaltySearchInputs = new Subject();
  headerReloadClick = new Subject<void>();
  getHeaderReloadClick$ = this.headerReloadClick.asObservable();

  setAppMode(mode: AppMode) {
    this.appMode = mode;
    localStorage.setItem(this.appModeStorageKey, mode);
    this.appModeSource.next(mode);
  }

  switchAppMode(mode: AppMode): Observable<boolean> {
    if (mode === "REPORTING") {
      this.activateReportingMode();
      return of(true);
    }

    if (this.hasValidBackOfficeToken()) {
      this.activateBackOfficeMode();
      return of(true);
    }

    return this.http
      .post<any>(appConfig.backOfficeLogIn, {
        companyId: 1,
        loginId: "admin",
        password: "1234",
      })
      .pipe(
        map((response) => {
          if (!response?.token) {
            return false;
          }

          localStorage.setItem(
            this.backOfficeTokenStorageKey,
            JSON.stringify(response),
          );
          this.activateBackOfficeMode();
          return true;
        }),
        catchError(() => of(false)),
      );
  }

  private activateReportingMode(): void {
    this.setAppMode("REPORTING");

    if (!localStorage.getItem("loggedInUser")) {
      this.router.navigate(["/login"]);
      return;
    }

    const storedRoute = localStorage.getItem("currentRouteUrl");
    const reportingRoute =
      storedRoute &&
      !storedRoute.startsWith("/backOffice") &&
      !storedRoute.startsWith("/login")
        ? storedRoute
        : "/sales/locationItems";
    this.router.navigateByUrl(reportingRoute);
  }

  private activateBackOfficeMode(): void {
    this.setAppMode("BACK_OFFICE");
    this.router.navigate(["/backOffice"]);
  }

  private getStoredAppMode(): AppMode {
    return localStorage.getItem(this.appModeStorageKey) === "BACK_OFFICE"
      ? "BACK_OFFICE"
      : "REPORTING";
  }

  private hasValidBackOfficeToken(): boolean {
    try {
      const storedToken = JSON.parse(
        localStorage.getItem(this.backOfficeTokenStorageKey) || "null",
      );
      const token = storedToken?.token;

      if (!token) {
        return false;
      }

      const payload = JSON.parse(atob(token.split(".")[1]));
      return Number(payload.exp) * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * To update tree node data
   * @param data Node data
   */
  updateNode(data) {
    this.deleteNodeSource.next(data);
  }

  updateCompanyMenuItems(updateItems: boolean) {
    this.updateCompanyMenu.next(updateItems);
  }

  changeSwitchUserState() {
    this.switchUserSource.next(false);
  }

  redirectToLogin() {
    localStorage.clear();
    this.changeSwitchUserState();
    this.router.navigate(["/login"]);
  }

  /**
   * To update tree
   * @param data Tree data
   */
  updateTree(data) {
    this.treeSource.next(data);
  }

  /**
   * To share level data between components
   * @param data Levels data
   */
  updateLevels(data) {
    this.levelSource.next(data);
  }

  /**
   * To update user name in the header
   * @param data Updated Name
   */
  updateUserName(data) {
    this.userNameSource.next(data);
  }

  /**
   * To update company logo in the sidebar
   * @param data Updated Logo
   */
  updateCompanyLogo(data) {
    this.companyLogoSource.next(data);
  }

  /**
   * To update navigation state
   * @param data Updated state
   */
  updateNavState(data) {
    this.navigationStateSource.next(data);
  }

  /**
   * To update user profile image in header
   * @param data Updated image url
   */
  updateUserProfileImage(data) {
    this.userProfileImageSource.next(data);
  }

  /**
   * To share company data between sidebar and company settings page
   * @param data Company Data
   */
  updateCompanyData(data) {
    this.companyDataSource.next(data);
  }

  /**
   * To share user profile data between header and user profile page
   * @param data User profile data
   */
  updateUserData(data) {
    this.userDataSource.next(data);
  }

  /**
   * To update user name in the header
   * @param data Updated Name
   */

  /**
   * To update the language based on country code
   * @param data Language to display
   */
  updateCountryCode(data) {
    this.countryCodeSource.next(data);
  }

  /**
   * To update the skeleton loaders state on error
   */
  notifyError() {
    this.errorSubject.next();
  }

  loggingIn() {
    this.loggingInSubject.next();
  }
}
